import request, { authOptions } from 'api/requestUtils'
import { convertTZ } from 'lib/datetime/dateUtils'
import { extractZendeskTicketId } from 'lib/zendesk/zendeskUtils'
import moment from 'moment'

interface GenerateCallTokenResponse {
  result: {
    callToken: string
  }
}

/**
 * Generates a call token from svc-zendesk to be appended to phone numbers
 * @returns Promise containing the call token or null if request fails
 */
export async function generateZendeskCallToken(): Promise<string | null> {
  try {
    const response = await request.get<GenerateCallTokenResponse>(
      '/api/zendesk/generate-call-token',
      { credentials: 'include' },
    )
    return response.result.callToken || null
  } catch(error: unknown) {
    // If request fails, return null and allow flow to continue without token
    console.warn(`Failed to generate Zendesk call token: ${error instanceof Error ? error.message : 'Unknown error'}`)
    return null
  }
}

/**
 * Fetches ticket encoded ID from svc-zendesk
 * @param ticketId - The Zendesk ticket ID
 * @returns Promise containing the encoded ticket ID
 */
export async function getEncodedZendeskTicketId(
  ticketId: string,
): Promise<string | null> {
  try {
    const response = await request.get<{ result: { encodedId: string } }>(
      `/api/zendesk/ticket/${ticketId}`,
      authOptions(),
    )
    return response.result.encodedId || null
  } catch(error) {
    console.error('Error fetching Zendesk ticket:', error)
    return null
  }
}
interface UpdateZendeskTicketParams {
  ticketId: string
  quoteId: string
  customerCallbackTime?: string
  isSpoofed: boolean
  accessToken?: string
  customerTimezone?: string
}

export async function updateZendeskTicket({
  ticketId,
  quoteId,
  customerCallbackTime,
  isSpoofed,
  accessToken,
  customerTimezone,
}: UpdateZendeskTicketParams): Promise<void> {
  try {
    const headerOptions = isSpoofed ? authOptions(accessToken) : {}
    const extractedTicketId = extractZendeskTicketId(ticketId)

    if (!extractedTicketId) {
      throw new Error('Invalid Zendesk ticket ID')
    }

    let utcTime: string | undefined = undefined

    // this calculation might cause issues around daylight savings boundaries, the next one being in NZ on 24th Sep 25
    if (customerCallbackTime && customerTimezone) {
      const callbackWallTime = moment(customerCallbackTime, 'YYYY-MM-DD HH:mm').toDate()
      const wallTimeInCustomerTimezone = convertTZ(callbackWallTime, customerTimezone)
      const difference = callbackWallTime.getTime() - wallTimeInCustomerTimezone.getTime()
      utcTime = moment(callbackWallTime).add(difference, 'milliseconds').toISOString()
    }

    await request.patch(
      `/api/zendesk/ticket/${extractedTicketId}/quote`,
      {
        quoteIdToAdd: quoteId,
        agentTimezone: utcTime ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined,
        customerTimezone: customerTimezone || undefined,
        callbackDateTimeUTC: utcTime,
      },
      headerOptions,
    )
  } catch(error) {
    console.error('Error updating Zendesk ticket:', error)
  }
}
